import classnames from "classnames";
import omit from "omit.js"
import React, { useState, useEffect, useCallback, useRef } from "react";
import {prefix, WebUI, getNid} from "../../wui-core/src/index";
import Icon from '../../wui-icon/src';
import KeyCode from "./KeyCode.js";
import PopMenu from './popmenu/PopMenu'
import ScrollableInkTabBar from "./ScrollableInkTabBar";
import TabContent from "./TabContent";
import TabPane from "./TabPane";
import { TabsProps } from './iTabs';
import ReactDOMCompat from '../../wui-core/src/reactCompat';
import { getDefaultActiveKey } from "./utils";

function noop() {
}

const defaultProps = {
    clsPrefix: `${prefix}-tabs`,
    destroyInactiveTabPane: false,
    onChange: noop,
    tabBarPosition: "top",
    style: {},
    renderTabBar: () => <ScrollableInkTabBar/>,
    type: "simple",
    animated: true,
    defaultActiveKey: undefined,
    moreType: 'moreTabsSelect',
    onTabClick: () => {
    },
    className: '',
    hideAdd: false,
    onEdit: () => {
    },
    dragable: false,
  	onDrag: ()=>{
    },
    trigger: ['click'],
    centered: false
};

interface TabsComponent extends React.FC<TabsProps> {
    TabPane: typeof TabPane;
}

const Tabs: TabsComponent = (props) => {
    // 模拟 getDerivedStateFromProps - 在render期间同步计算
    const computedActiveKey = props.activeKey || props.defaultActiveKey || getDefaultActiveKey(props);
    const [activeKey, setActiveKey] = useState<string | undefined>(computedActiveKey);
    const [contextmenuType, setContextmenuType] = useState<Record<string, any> | null>(null);

    // 使用 useRef 管理实例变量
    const isEditNum = useRef(0);
    const tabNavs = useRef<any>(null);
    const tabspopmenu = useRef<any>(null);
    const tabBar = useRef<any>(null);
    const nodeKey = useRef<any>(null);
    const targetNodeBox = useRef<HTMLElement | null>(null);
    const tabsBox = useRef<HTMLDivElement | null>(null);
    const prevPropsRef = useRef<TabsProps>(props);

    // 添加防止重复调用的控制
    const isShowingPopMenu = useRef(false);
    // eslint-disable-next-line no-undef
    const showPopMenuTimer = useRef<NodeJS.Timeout | null>(null);

    // 模拟 getDerivedStateFromProps 的同步行为
    // 这必须在render期间同步执行，不能使用effect
    if ("activeKey" in props && props.activeKey != activeKey) {
        setActiveKey(props.activeKey);
    }

    // 处理 componentDidMount 和 componentWillUnmount
    useEffect(() => {
        bindContextMenuEvents();
        return () => {
            unbindContextMenuEvents();
            // 清理定时器
            if (showPopMenuTimer.current) {
                clearTimeout(showPopMenuTimer.current);
                showPopMenuTimer.current = null;
            }
            isShowingPopMenu.current = false;
        };
    }, []);

    // 模拟 componentDidUpdate - 完整复现Class组件的行为
    useEffect(() => {
        const prevProps = prevPropsRef.current;
        unbindContextMenuEvents();
        bindContextMenuEvents();

        // 3. 处理children长度变化（对应原代码的componentDidUpdate）
        const currentChildrenLength = (props.children as any)?.length;
        const prevChildrenLength = (prevProps.children as any)?.length;
        if (currentChildrenLength !== prevChildrenLength && currentChildrenLength !== undefined) {
            isEditNum.current = currentChildrenLength;
        }

        // 更新prevProps引用
        prevPropsRef.current = props;
    });

    const getTargetNodeKey = useCallback((target: any): void => {
        if (target.getAttribute('nodekey')) {
            nodeKey.current = target.getAttribute('nodekey');
            targetNodeBox.current = target;
        } else {
            getTargetNodeKey(target.parentNode);
        }
    }, []);

    const contextMenuHandler = useCallback((event: any) => {
        if (!props.popMenu) return;
        event.preventDefault();
        event.stopPropagation();

        // 如果正在显示弹出菜单，防止重复调用
        if (isShowingPopMenu.current) {
            return;
        }

        let target = event.target;
        let key = ''; // 获取右击标签页的key
        // let closeable; // 判断标签页是否可关闭

        // 某一项设置了disabled属性时不做交互
        if (target.ariaDisabled == true || target.ariaDisabled == 'true') {
            return;
        }
        let tabs = props.children ? props.children as any : [];
        if (!Array.isArray(tabs)) {
            tabs = [props.children];
        }
        getTargetNodeKey(target);
        key = nodeKey.current;

        // 绝对定位计算menu X轴位置
        let leftNum = 0;
        let tabHeight = 0;
        let tabNavsNode = Array.prototype.slice.call(tabNavs.current); // 兼容ie处理
        // 获取下拉位置，兼容多页签情况存在translate属性时，准确获取位置
        leftNum = targetNodeBox.current!.getBoundingClientRect().left - tabsBox.current!.getBoundingClientRect().left;
        tabHeight = tabNavsNode[0]?.getBoundingClientRect().height;

        // 清除之前的定时器
        if (showPopMenuTimer.current) {
            clearTimeout(showPopMenuTimer.current);
            showPopMenuTimer.current = null;
        }

        // 显示弹出菜单
        let { trigger } = props;
        const showMenu = () => {
            // 设置标志位，防止重复调用
            isShowingPopMenu.current = true;
            tabspopmenu.current?.show(event, key, leftNum, tabHeight);
            // 短暂延迟后重置标志位
            setTimeout(() => {
                isShowingPopMenu.current = false;
            }, 100);
        };

        if (trigger!.includes('hover')) { // hover状态要求300ms延时显示下拉
            showPopMenuTimer.current = setTimeout(() => {
                showMenu();
            }, 300);
        } else {
            showMenu();
        }
    }, [props.popMenu, props.children, props.trigger, getTargetNodeKey]);

    // 事件委托处理器 - 优化版本
    const delegatedEventHandler = useCallback((event: any) => {
        const { popMenu, clsPrefix } = props;
        if (!popMenu) return;

        // 使用事件委托，从目标元素找到对应的 tab 元素
        const target = event.target as HTMLElement;
        const tabElement = target.closest(`.${clsPrefix}-tab`);

        // 如果不是 tab 元素或已禁用，直接返回
        if (!tabElement || tabElement.getAttribute('aria-disabled') === 'true') {
            return;
        }

        // 保存当前 tab 元素信息，供 contextMenuHandler 使用
        tabNavs.current = [tabElement];
        contextMenuHandler(event);
    }, [props.popMenu, props.clsPrefix, contextMenuHandler]);

    // 绑定右键弹出菜单 - 使用事件委托优化版本
    const bindContextMenuEvents = useCallback(() => {
        const { trigger, popMenu } = props;
        if (!popMenu || !tabsBox.current) return;

        // 使用事件委托，只在容器上绑定一次事件
        if (trigger?.includes('hover') && !trigger?.includes('click')) {
            // 只有 hover 触发
            tabsBox.current.addEventListener('mouseenter', delegatedEventHandler, true);
            tabsBox.current.removeEventListener('contextmenu', delegatedEventHandler);
        } else if (trigger?.includes('hover') && trigger?.includes('click')) {
            // hover 和 click 都触发
            tabsBox.current.addEventListener('contextmenu', delegatedEventHandler);
            tabsBox.current.addEventListener('mouseenter', delegatedEventHandler, true);
        } else {
            // 默认右键触发
            tabsBox.current.addEventListener('contextmenu', delegatedEventHandler);
            tabsBox.current.removeEventListener('mouseenter', delegatedEventHandler, true);
        }
    }, [props.trigger, props.popMenu, delegatedEventHandler]);

    // 解绑定右键弹出菜单 - 优化版本
    const unbindContextMenuEvents = useCallback(() => {
        if (!tabsBox.current) return;

        // 移除事件委托的监听器
        tabsBox.current.removeEventListener('contextmenu', delegatedEventHandler);
        tabsBox.current.removeEventListener('mouseenter', delegatedEventHandler, true);
    }, [delegatedEventHandler]);


    // 下拉显示隐藏回调
    const onPopMenuIsShow = useCallback((open: boolean) => {
        let { onPopMenu } = props;
        if (onPopMenu) {
            // 使用requestAnimationFrame延迟执行，避免在render过程中触发状态更新
            const callback = onPopMenu; // 保存到局部变量，确保类型安全
            requestAnimationFrame(() => {
                callback(open, nodeKey.current); // 第一个参数为下拉框的显隐状态，第二个参数为触发下拉显隐的tab页签key
            });
        }
    }, [props.onPopMenu]);

    // 右键弹出菜单相关回调
    const handleMenuClick = useCallback((type: string, key: string) => {
        let obj = {type, tabKey: key};
        let {onPopMenuClick} = props;
        if (onPopMenuClick) {
            onPopMenuClick(obj);
        }
        setContextmenuType(obj);
    }, [props.onPopMenuClick]);

    // 构建右键弹出菜单
    const createContextMenuItems = useCallback(() => {
        let { clsPrefix, popMenu, dir } = props;
        let menuItems = null;
        if (popMenu && Array.isArray(popMenu()) && popMenu().length > 0) {
            menuItems = popMenu(nodeKey.current);
        } else {
            return;
        }
        return <PopMenu dir={dir} ref={tabspopmenu} onMenuClick={handleMenuClick} items={menuItems} clsPrefix={clsPrefix} onPopMenuIsShow={onPopMenuIsShow} />;
    }, [props.clsPrefix, props.popMenu, props.dir, handleMenuClick, onPopMenuIsShow]);

    const setActiveKeyFunc = useCallback((newActiveKey: string) => {
        if (activeKey !== newActiveKey) {
            if (!("activeKey" in props)) {
                setActiveKey(newActiveKey);
            }
            props.onChange!(newActiveKey);
        }
    }, [activeKey, props.onChange]);

    const getNextActiveKey = useCallback((next: boolean) => {
        const children: any[] = [];
        React.Children.forEach(props.children, (c: React.ReactElement) => {
            if (c && !c.props.disabled) {
                if (next) {
                    children.push(c);
                } else {
                    children.unshift(c);
                }
            }
        });
        const length = children.length;
        let ret = length && children[0].key;
        children.forEach((child, i) => {
            if (child.key === activeKey) {
                if (i === length - 1) {
                    ret = children[0].key;
                } else {
                    ret = children[i + 1].key;
                }
            }
        });
        return ret;
    }, [activeKey, props.children]);

    const onTabClick = useCallback((activeKey: string) => {
        props.onTabClick && props.onTabClick(activeKey);
        if (tabBar.current?.props?.onTabClick) {
            tabBar.current.props.onTabClick(activeKey);
        }
        ReactDOMCompat.startTransition(() => {
            setActiveKeyFunc(activeKey);
        });
    }, [props.onTabClick, setActiveKeyFunc]);

    const onNavKeyDown = useCallback((e: React.KeyboardEvent) => {
        const eventKeyCode = e.keyCode;
        if ((eventKeyCode === KeyCode.RIGHT && props.dir !== 'rtl') || eventKeyCode === KeyCode.DOWN || (eventKeyCode === KeyCode.LEFT && props.dir === 'rtl')) {
            e.preventDefault();
            const nextKey = getNextActiveKey(true);
            onTabClick(nextKey);
        } else if ((eventKeyCode === KeyCode.LEFT && props.dir !== 'rtl') || eventKeyCode === KeyCode.UP || (eventKeyCode === KeyCode.RIGHT && props.dir === 'rtl')) {
            e.preventDefault();
            const previousKey = getNextActiveKey(false);
            onTabClick(previousKey);
        }
    }, [props.dir, getNextActiveKey, onTabClick]);

    const onPrevClick = useCallback((e: React.MouseEvent) => {
        let {tabBarPosition, onPrevClick, onTabScroll} = props;
        if (['top', 'bottom'].includes(tabBarPosition as string)) {
            onTabScroll && onTabScroll({direction: 'left'}, e);
        } else {
            onTabScroll && onTabScroll({direction: 'top'}, e);
        }
        onPrevClick && onPrevClick(e);
    }, [props.tabBarPosition, props.onPrevClick, props.onTabScroll]);

    const onNextClick = useCallback((e: React.MouseEvent) => {
        let {tabBarPosition, onNextClick, onTabScroll} = props;
        if (['top', 'bottom'].includes(tabBarPosition as string)) {
            onTabScroll && onTabScroll({direction: 'right'}, e);
        } else {
            onTabScroll && onTabScroll({direction: 'bottom'}, e);
        }
        onNextClick && onNextClick(e);
    }, [props.tabBarPosition, props.onNextClick, props.onTabScroll]);
    const createNewTab = useCallback((targetKey: string) => {
        const {onEdit, children} = props;
        isEditNum.current = (children as any[])?.length;
        if (onEdit) {
            onEdit(targetKey, 'add');
        }
    }, [props.onEdit, props.children]);

    const removeTab = useCallback((targetKey: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!targetKey) {
            return;
        }
        React.Children.forEach(props?.children, (child: React.ReactElement, index) => {
            if (child?.key === targetKey) {
                isEditNum.current = index;
            }
        });

        const {onEdit} = props;
        if (onEdit) {
            onEdit(targetKey, 'remove');
        }
    }, [props.onEdit, props.children]);

    // Render 部分
    const {
        clsPrefix,
        tabBarPosition,
        className,
        renderTabContent = () => <TabContent/>,
        renderTabBar,
        tabBarStyle,
        type,
        extraContent,
        animated,
        tabIndex,
        children,
        hideAdd,
        dir,
        inkBarAnimated,
        addIcon,
        items,
        centered,
        fieldid,
        ...others
    } = props;

    const cls = classnames({
        [`${clsPrefix}`]: true,
        [`${clsPrefix}-${tabBarPosition}`]: true,
        [`${className}`]: !!className,
        [`${clsPrefix}-${type}`]: true,
        [`${clsPrefix}-centered`]: centered
    });
    const renderProps = {
        ...props,
        children: null,
        inkBarAnimated,
        extraContent: extraContent,
        className: cls,
    };
    if (renderTabBar) {
        tabBar.current = renderTabBar(renderProps, ScrollableInkTabBar);
    } else {
        tabBar.current = <ScrollableInkTabBar {...renderProps} />;
    }

	    let childrenWithClose: any[] = [],
	        tabBarExtraContent = extraContent;
	    if (type === 'editable-card') {
	        childrenWithClose = [];
	        // 使用items属性时，在type为editable-card类型是要处理关闭图标相关逻辑
	        if (items && items?.length > 0) {
	            items?.map((item, index) => {
	                let {closable, closeIcon} = item;
	                closable = typeof closable === 'undefined' ? true : closable;
	                const closeIconDom = closable ? closeIcon ? closeIcon : (
	                    <Icon
	                        type="uf-close"
	                        className={`${clsPrefix}-close-x`}
	                        fieldid={fieldid ? fieldid + '_edit_close_' + index : undefined}
	                    />
	                ) : null;
	                childrenWithClose.push(
	                    React.cloneElement(<TabPane>{item.children}</TabPane>, {
	                        // @ts-ignore
	                        tab: (
	                            <div className={closable ? undefined : `${clsPrefix}-tab-unclosable`}>
	                                {item.tab}
	                                <span onClick={e => removeTab(item.key as string, e)}>
	                                    {closeIconDom}
	                                </span>
	                            </div>
	                        ),
	                        key: item.key || index,
	                        disabled: item?.disabled,
	                    }),
	                );
	            })
	        } else {
	            React.Children.forEach(children, (child: any, index) => {
	                if (!React.isValidElement(child)) return child;
	                let {closable, closeIcon} = child.props as TabsProps;
	                closable = typeof closable === 'undefined' ? true : closable;
	                const closeIconDom = closable ? closeIcon ? closeIcon : (
	                    <Icon
	                        type="uf-close"
	                        className={`${clsPrefix}-close-x`}
	                        fieldid={fieldid ? fieldid + '_edit_close_' + index : undefined}
	                    />
	                ) : null;
	                childrenWithClose.push(
	                    React.cloneElement(child, {
	                        // @ts-ignore
	                        tab: (
	                            <div className={closable ? undefined : `${clsPrefix}-tab-unclosable`}>
	                                {(child as React.ReactElement).props.tab}
	                                <span onClick={e => removeTab(child.key as string, e)}>
	                                    {closeIconDom}
	                                </span>
	                            </div>
	                        ),
	                        key: child.key || index,
	                    }),
	                );
	            });
	        }
	        // Add new tab handler
	        if (!hideAdd) {
	            const newTabIcon = addIcon ? React.cloneElement(addIcon, {
	                onClick: createNewTab,
	                className: `${clsPrefix}-new-tab`
	            }) : <Icon type="uf-plus" className={`${clsPrefix}-new-tab`} onClick={createNewTab}/>;
	            tabBarExtraContent = <span>{newTabIcon}{extraContent}</span>;
	        }
	    } else if (type === 'trapezoid') {
	        React.Children.forEach(children, (child: any, index) => {
	            if (!React.isValidElement(child)) return child;
	            childrenWithClose.push(
	                React.cloneElement(child, {
	                    // @ts-ignore
	                    tab: (
	                        <div className={`${clsPrefix}-tab-item`}>
	                           	{(child as React.ReactElement).props.tab}
	                        </div>
	                    ),
	                    key: child.key || index,
	                }),
	            );
	        });
	    } else if (type === 'fill-line') {
	        React.Children.forEach(children, (child: any, index) => {
	            if (!React.isValidElement(child)) return child;
	            childrenWithClose.push(
	                React.cloneElement(child, {
	                    // @ts-ignore
	                    tab: (
	                        <div className={classnames({
                            [`${clsPrefix}-tab-item`]: true,
                            [`${clsPrefix}-fill-line-active`]: activeKey === child.key && typeof (child as React.ReactElement).props.tab === 'function'
                        })}>
	                            {typeof (child as React.ReactElement).props.tab === 'function'
	                                ? (child as React.ReactElement).props.tab(<div className="wui-tabs-text-content"></div>, child.key)
	                                : (child as React.ReactElement).props.tab}
	                        </div>
	                    ),
	                    key: child.key || index,
	                }),
	            );
	        });
	    }

    const contents = [
        React.cloneElement(tabBar.current, {
            clsPrefix,
            dir,
            key: "tabBar",
            onKeyDown: onNavKeyDown,
            tabBarPosition,
            extraContent: tabBarExtraContent,
            onTabClick: onTabClick,
            panels: childrenWithClose.length > 0 ? childrenWithClose : children,
            activeKey: activeKey,
            tabIndex,
            onPrevClick: onPrevClick,
            onNextClick: onNextClick,
            type,
            tabBarStyle: tabBarStyle,
            moreType: props.moreType,
            contextmenuType: contextmenuType,
            tabBarClassName: props.tabBarClassName,
            fieldid: props.fieldid,
            id: props.id,
            dragable: props.dragable,
            onDrag: props.onDrag,
            isEditNum: isEditNum.current,
            onEdit: props.onEdit,
            moreIcon: props.moreIcon,
            items: type === 'trapezoid' || type === 'fill-line' ? props.items || [] : childrenWithClose.length > 0 ? childrenWithClose : props.items,
            dropdownClassName: props.dropdownClassName,
            moreTypeAllShow: props.moreTypeAllShow,
            isTruncationShow: props.isTruncationShow
        }),
        React.cloneElement(renderTabContent!() as React.ReactElement, {
            clsPrefix,
            dir,
            tabBarPosition,
            animated,
            activeKey: activeKey,
            destroyInactiveTabPane: props.destroyInactiveTabPane,
            children: props.children,
            onChange: setActiveKeyFunc,
            key: "tabContent",
            tabContentClassName: props.tabContentClassName,
            items: props.items,
        })
    ];
    if (tabBarPosition === "bottom") {
        contents.reverse();
    }
    let adapterNid = getNid(props);
    return (
        <div className={cls} fieldid={props.fieldid} ref={tabsBox} style={props.style} {...omit(others, ["moreType", "onTabScroll", "activeKey", "defaultActiveKey", "scrollAnimated", "destroyInactiveTabPane", "onTabClick", "onEdit", "onNextClick", "onPrevClick", "onChange", "moreIcon", "tabContentClassName", "tabBarExtraContent"])} {...adapterNid}>
            {contents}
            {createContextMenuItems()}
        </div>
    );
};

// 添加静态属性
Tabs.TabPane = TabPane;

// 应用装饰器
const TabsWithWebUI = WebUI({name: "tabs", defaultProps})(Tabs);

export {TabsWithWebUI as Tabs};
